import { isArray, isNumber, isString, toInteger, toNumber } from "lodash"
import {
  DEFAULT_SIZE,
  DOT_SHAPE,
  LEVEL_ONE,
  SOLID_LINE
} from "src/blitz/Charts/constants"
import {
  chartDateFormat,
  constantValues,
  editableProperties,
  reduxStoreTimeStampFormat
} from "../../constant"
import {
  assignAttributeStateToLegends,
  assignAttributeStateToTLLegends,
  convertTimeToMinutes,
  generateLegend,
  getCalculatedAndMinInterval,
  getPropertyAxisAndIsAdded,
  getTimeIndex,
  intervalToMinutes,
  legendName
} from "../common"
import moment from "moment"
import {
  TIMELINE_PROPERTY_TYPE,
  transformTimeLinechartNew
} from "./timeLineChart"
import { LINE_TYPE, MEAN_LINE_TYPE } from "./lineChart"
import { generateExceptionBars } from "../Exceptions/index"
import { generateExceptionLegends } from "../Exceptions/helper"
import { generateSuppressionLegends } from "../Suppressions/helper"
import { generateSuppressionBars } from "../Suppressions"

export const MEAN_OF_ALL_ID = "meanOfAll"
export const transformLineWithControlRangeAndMeanchart = ({
  tz,
  startDate,
  endDate,
  intervalMinutes = "15m",
  inputData,
  chartInfo,
  equipmentList,
  equipmentId,
  addedProperties,
  oldLegends,
  oldTimeLineLegends = null,
  assignedAttributes = [],
  legendAttributesCombination,
  only15Min
}) => {
  const defaultBoundaryRange = {
    above: 95,
    upper: 65,
    low: 0
  }
  let calculatedMin = intervalToMinutes(intervalMinutes)
  let minimumCalculatedMin = intervalToMinutes(intervalMinutes)
  let minInterval = calculatedMin
  const defaultCalculatedMin = intervalToMinutes(intervalMinutes)
  let lastIndexPosition = (60 / calculatedMin) * 24 - 1
  const data = inputData?.data || {}
  const equipmentType = chartInfo?.equipmentType
  const settings = inputData?.settings || {}
  const addedAxisList = addedProperties?.axisList || []
  const legends = {}
  let timeLineLegends = {}
  const additionalInformation = {}
  const newData = {}
  const allTimestampsObject = {}
  const localTimeStamp = Boolean(tz)
    ? moment(
        moment.tz(moment(), tz).format("MM-DD-YYYY HH:mm"),
        "MM-DD-YYYY HH:mm"
      )
    : moment()
  const endTimestamp = moment(endDate, chartDateFormat).add(1, "days")
  for (const id in data) {
    if (id === MEAN_OF_ALL_ID) {
      continue
    }
    const paramData = data[id]
    const equipmentName = equipmentList?.find((obj) => obj?.id === id)?.name
    const selectedEquipment = equipmentId === id
    const properties = paramData?.properties
    const legendEquipmentType = equipmentList?.find(
      (obj) => obj?.id === id
    )?.type
    const sortedDates = Object.keys(paramData?.values || {})
      .filter((date) => date != "frq")
      .sort(
        (a: string, b: string) =>
          new Date(a).getTime() - new Date(b).getTime()
      )

    for (const propertyKey in properties) {
      calculatedMin = defaultCalculatedMin
      minInterval = defaultCalculatedMin
      lastIndexPosition = (60 / minInterval) * 24 - 1
      const property = properties[propertyKey]
      const propertyIndex = property?.index
      const propertyName = property?.name || ""

      const { axis, isUserAddedProperty, legendId } = getPropertyAxisAndIsAdded(
        {
          chartInfo,
          propertyKey,
          equipmentType,
          equipmentList,
          id,
          addedAxisList
        }
      )
      let legendObj: any = {}

      if (axis?.axisName === "timeline") {
        const name = legendName({
          selectedEquipment,
          propertyName,
          equipmentName
        })
        legendObj = generateLegend({
          legendId,
          name,
          axisName: axis?.axisName,
          symbol: axis?.symbol,
          propertyKey,
          type: "timeline",
          isUserAddedProperty,
          oldLegends: oldTimeLineLegends,
          propertyType: TIMELINE_PROPERTY_TYPE
        })
        timeLineLegends[legendId] = legendObj
      } else {
        const name = legendName(
          { selectedEquipment, propertyName, equipmentName },
          [isUserAddedProperty ? true : false, true]
        )
        legendObj = generateLegend({
          legendId,
          name,
          axisName: axis?.axisName,
          symbol: axis?.symbol,
          type: LINE_TYPE,
          isUserAddedProperty,
          legendEquipmentType,
          oldLegends
        })
        legends[legendId] = legendObj
      }
      let currentTimestamp = moment(startDate, chartDateFormat).locale("en")

      let prevValue = null
      while (
        currentTimestamp.isSameOrBefore(endTimestamp) &&
        currentTimestamp.isSameOrBefore(localTimeStamp)
      ) {
        const dateKey = currentTimestamp.format("YYYY-MM-DD")
        const extractTime = currentTimestamp.format("HH:mm")
        const timetoMin = convertTimeToMinutes(extractTime)

        const dateAvailable = paramData?.values?.[dateKey]
        if (dateAvailable) {
          const dateIndexPosition = sortedDates.indexOf(dateKey)
          const extractedInterval =
            paramData.values?.frq?.[dateIndexPosition]?.split("|")?.[
              propertyIndex
            ]
          const intervals = getCalculatedAndMinInterval({
            only15Min,
            axis,
            defaultCalculatedMin,
            extractedInterval,
            paramData,
            dateKey
          })
          calculatedMin = intervals.calculatedMin
          minInterval = intervals.minInterval
          lastIndexPosition = (60 / minInterval) * 24 - 1

          if (calculatedMin < minimumCalculatedMin) {
            minimumCalculatedMin = calculatedMin
          }
        }

        const timeIndexPosition = getTimeIndex(timetoMin, minInterval)
        let finalValue
        if (dateAvailable) {
          const valueArray = paramData?.values?.[dateKey]?.[timeIndexPosition]
          const valueArrayIsArray = isArray(valueArray)
          const arrayValues = valueArrayIsArray ? valueArray : []
          let value = arrayValues[propertyIndex]

          const isOutOfRange = value?.toString()?.startsWith("_")
          if (isOutOfRange && value?.toString()?.length > 1) {
            value = value?.toString()?.substring(1)
          }

          const isNull = constantValues?.includes(value)
          const isTimeline = axis?.axisName === "timeline"

          const checkedValue =
            isNull || (isTimeline ? false : !isFinite(toNumber(value)))
              ? null
              : isTimeline
              ? value
              : parseFloat(value)

          if ((isString(value) && value?.length === 0) || !valueArrayIsArray) {
            finalValue = prevValue
          } else {
            finalValue = checkedValue
          }

          if (timeIndexPosition === lastIndexPosition) {
            prevValue = null
          } else {
            prevValue = finalValue
          }
        } else {
          finalValue = null
          prevValue = null
        }
        const dateVal = currentTimestamp
          .locale("en")
          .format(reduxStoreTimeStampFormat)

        if (!newData?.[legendId]) {
          newData[legendId] = {
            data: [],
            timestamps: []
          }
        }
        newData[legendId]?.data.push(finalValue)
        newData[legendId]?.timestamps.push(dateVal)
        if (!allTimestampsObject[dateVal]) {
          allTimestampsObject[dateVal] = 1
        }
        currentTimestamp = currentTimestamp.add(calculatedMin, "minutes")
      }
    }
  }
  const allTimestamps = Object.keys(allTimestampsObject).sort()
  const mean = []
  let timestampsMean = []
  const arrayOfArrays = []
  Object.keys(legends)?.forEach((key) => {
    const legend = legends?.[key]
    if (
      legend?.type === "line" &&
      !legend?.isUserAddedProperty &&
      key !== MEAN_OF_ALL_ID
    ) {
      const data = newData?.[legend?.id]?.data || []
      const hasAnyData = data?.some((e) => isNumber(e) && !isNaN(e))

      if (hasAnyData) {
        timestampsMean = newData?.[legend?.id]?.timestamps
        arrayOfArrays.push(data)
      }
    }
  })

  const paramCount = arrayOfArrays?.length
  const numOfColumns = arrayOfArrays?.[0]?.length || 0
  for (let j = 0; j < numOfColumns; j++) {
    let columnSum = 0
    let validCount = 0

    for (let i = 0; i < paramCount; i++) {
      const val = arrayOfArrays?.[i]?.[j]
      if (isNumber(val) && !isNaN(val)) {
        columnSum += val
        validCount++
      }
    }
    mean[j] =
      validCount > 0 && isFinite(toNumber(columnSum))
        ? Math.round(columnSum / validCount)
        : null
  }

  const noDataArray = Object.keys(legends)?.map((key) => {
    const noData = (newData?.[key]?.data || [])?.every(
      (e) => !Boolean(e) && e !== 0
    )
    return noData
  })

  const isNoData = noDataArray?.every((e) => Boolean(e))

  assignAttributeStateToLegends(
    legends,
    oldLegends,
    assignedAttributes,
    legendAttributesCombination
  )

  const axisList = chartInfo?.axisConfig?.filter(
    (obj) => obj?.axisName !== "timeline" && obj?.axisName !== "exceptions"
  )

  const finalAxisList = [...axisList]

  addedAxisList?.forEach((newObj) => {
    const axisAvailable = finalAxisList?.find(
      (obj) => obj?.axisName === newObj?.axisName
    )
    if (!axisAvailable) {
      finalAxisList.push(newObj)
    }
  })

  // Create/update Exception Legends
  const exceptionData = inputData?.exceptions?.data || {}
  const exceptionLegends = generateExceptionLegends({
    exceptionData,
    chartInfo,
    equipmentList,
    selectedEquipID: equipmentId,
    oldTimeLineLegends
  })

  // Create/update Suppression Legends
  const suppressionData = inputData?.exceptions?.suppressed?.data || {}
  const suppressionLegends = generateSuppressionLegends({
    suppressionData,
    chartInfo,
    oldTimeLineLegends
  })

  // Assign Colors to new time line legends of all kinds
  assignAttributeStateToTLLegends(
    timeLineLegends,
    exceptionLegends,
    suppressionLegends,
    assignedAttributes,
    legendAttributesCombination,
    oldTimeLineLegends
  )

  const { timeLineBars, totalLanes } = transformTimeLinechartNew(
    timeLineLegends,
    newData
  )

  const { exceptionsBars, exceptionLanes } = generateExceptionBars(
    exceptionData,
    exceptionLegends,
    chartInfo,
    totalLanes,
    startDate,
    endTimestamp,
    localTimeStamp
  )

  const suppressed =
    (inputData?.exceptions?.suppressed?.data &&
      inputData?.exceptions?.suppressed?.data[equipmentId]) ||
    {}
  const { suppressionBars } = generateSuppressionBars(
    suppressed,
    suppressionLegends,
    exceptionLanes,
    startDate,
    endTimestamp.format("YYYY-MM-DD")
  )

  const bars = [...timeLineBars, ...exceptionsBars, ...suppressionBars]
  const isNoTimeLineData = bars?.every(
    (obj) => !Boolean(obj?.value) || obj?.value === "No Data"
  )

  timeLineLegends = {
    ...timeLineLegends,
    ...exceptionLegends,
    ...suppressionLegends
  }

  const meanLegendObj = {
    id: MEAN_OF_ALL_ID,
    name: "#$ Mean of All",
    color: "yellow",
    axisName: "y1",
    shapeSize: LEVEL_ONE,
    lineType: SOLID_LINE,
    shape: DOT_SHAPE,
    strokeWidth: LEVEL_ONE,
    show: true,
    visible: true,
    type: MEAN_LINE_TYPE,
    defaultSettings: {}
  }

  const keys = Object.keys(meanLegendObj) || []
  editableProperties?.forEach((attribute: string) => {
    if (keys?.includes(attribute)) {
      meanLegendObj.defaultSettings[attribute] = meanLegendObj?.[attribute]
    }
  })
  legends[meanLegendObj?.id] = meanLegendObj
  if (mean?.length > 0) {
    newData[MEAN_OF_ALL_ID] = {
      data: mean,
      timestamps: timestampsMean
    }
  }

  const propKeys = Object.keys(settings)
  if (propKeys?.length > 0) {
    additionalInformation["settings"] = {}
    let boundaryIndex = 0
    do {
      const key = propKeys[boundaryIndex - 1]?.toLowerCase()
      const initialIndex = boundaryIndex === 0
      const isLow = key?.includes("low")
      const settingsKey = initialIndex ? "low" : isLow ? "upper" : "above"
      const value = isNumber(toInteger(settings[propKeys[boundaryIndex - 1]]))
        ? settings[propKeys[boundaryIndex - 1]]
        : defaultBoundaryRange[settingsKey]
      const settingsValue = initialIndex ? 0 : value
      additionalInformation["settings"][settingsKey] = toNumber(settingsValue)
      const legendObj = {
        id: initialIndex ? "low" : key,
        name: initialIndex
          ? "#$ Lower Control Range"
          : isLow
          ? "#$ Upper Control Range"
          : "#$ Above Control Range",
        color: initialIndex ? "#87b1e1" : isLow ? "#67d25b" : "#ff001a",
        settingsKey: settingsKey,
        axisName: "y1",
        shapeSize: DEFAULT_SIZE,
        lineType: SOLID_LINE,
        shape: DOT_SHAPE,
        strokeWidth: LEVEL_ONE,
        show: true,
        visible: true,
        type: "boundary",
        boundaryPosition: initialIndex ? 0 : isLow ? 1 : 2,
        defaultSettings: {}
      }
      const keys = Object.keys(legendObj) || []
      editableProperties?.forEach((attribute: string) => {
        if (keys?.includes(attribute)) {
          legendObj.defaultSettings[attribute] = legendObj?.[attribute]
        }
      })
      legends[legendObj?.id] = legendObj
      boundaryIndex++
    } while (boundaryIndex <= propKeys?.length)
  } else {
    additionalInformation["settings"] = defaultBoundaryRange
  }

  additionalInformation["timeLineBars"] =
    isNoTimeLineData && isNoData ? [] : bars
  additionalInformation["timeLineLegends"] = timeLineLegends

  return {
    chartData: {
      data: isNoData && isNoTimeLineData ? {} : newData
    },
    chartProps: {
      legends,
      timestamps: allTimestamps,
      axisList: finalAxisList,
      toggleSwitch: true,
      noDataAvailable: isNoData && isNoTimeLineData ? true : false,
      isdataprocessed: true,
      calculatedInterval: minimumCalculatedMin,
      ...additionalInformation
    }
  }
}
